import { useMemo } from 'react';
import { Link } from 'react-router';
import { useLingui } from '@lingui/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { QueryErrorState } from '@/components/ui/query-error-state';
import { CoverageStatusBadge } from '@/components/shifts/CoverageStatusBadge';
import { isLiveCoverageUnavailable } from '@/hooks/useShiftCoverage';
import { formatNumber } from '@/lib/format';
import { formatOrgTime } from '@/utils/dateUtils';
import { compareCoverageSeverity } from '@/types/coverage';
import type { shifts_LiveGroupCoverageDTO } from '@/api/models/shifts_LiveGroupCoverageDTO';
import type { shifts_LiveCoverageResponse } from '@/api/models/shifts_LiveCoverageResponse';
import type { shifts_ShiftListResponse } from '@/api/models/shifts_ShiftListResponse';
import type { group_presence_OpenGroupPresenceResponse } from '@/api/models/group_presence_OpenGroupPresenceResponse';
import type { BlockSource } from '@/hooks/useDashboardSummary';
import { OnShiftNowRow } from './OnShiftNowRow';

interface LiveStaffingCardProps {
  /** Today's date in `yyyy-MM-dd`, for the link to the coverage tab. */
  today: string;
  live: BlockSource<shifts_LiveCoverageResponse>;
  shifts: BlockSource<shifts_ShiftListResponse>;
  presence: BlockSource<group_presence_OpenGroupPresenceResponse>;
  groupId?: string | null;
  /** The Dashboard's group scope for the people row; undefined without a filter. */
  inScope?: (groupId: string) => boolean;
  className?: string;
}

/**
 * Who is on shift right now against the children checked in, per group, from
 * `GET /shifts/coverage/live`, led by the people on shift now (#2175 C5). The
 * Dashboard passes the sources (`useDashboardSources`); its minute poll
 * refreshes the live ratio. Hidden for a user the endpoint refuses, because "no
 * access" is not something to act on here.
 */
export function LiveStaffingCard({ today, live, shifts, presence, groupId, inScope, className }: LiveStaffingCardProps) {
  const { i18n } = useLingui();

  const groups = useMemo(() => {
    const all = live.data?.groups ?? [];
    const scoped = groupId ? all.filter((g) => g.group_id === groupId) : all;
    // Understaffed groups first, so the one that needs a person is on top.
    return [...scoped].sort(
      (a, b) =>
        compareCoverageSeverity(a.status, b.status) || a.group_name.localeCompare(b.group_name, i18n.locale),
    );
  }, [live.data?.groups, groupId, i18n.locale]);

  if (isLiveCoverageUnavailable(live.error)) return null;

  const totals = groups.reduce(
    (sum, g) => ({ staff: sum.staff + g.present_staff, children: sum.children + g.present_children }),
    { staff: 0, children: 0 },
  );
  const asOf = live.data?.as_of ? formatOrgTime(live.data.as_of) : '';

  return (
    <Card className={className} data-testid="live-staffing-card">
      <CardHeader className="flex flex-row flex-wrap items-baseline justify-between gap-x-4 gap-y-1 space-y-0">
        <CardTitle className="text-base">
          {i18n._({ id: 'dashboard.live.title', message: 'On shift now' })}
        </CardTitle>
        {asOf && (
          <p className="text-xs text-muted-foreground tabular-nums">
            {i18n._({ id: 'dashboard.live.asOf', message: 'as of {time}', values: { time: asOf } })}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Who is on shift (#2175 C5); this card's minute refresh keeps the time current. */}
        <OnShiftNowRow shifts={shifts} presence={presence} inScope={inScope} />
        {live.isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : live.isError ? (
          <QueryErrorState error={live.error} onRetry={() => void live.refetch()} compact />
        ) : groups.length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground">
            {i18n._({ id: 'dashboard.live.empty', message: 'No groups to show.' })}
          </p>
        ) : (
          <>
            <p className="text-sm">
              {i18n._({
                id: 'dashboard.live.summary',
                message:
                  '{staff, plural, one {# person} other {# people}} on shift, {children, plural, one {# child} other {# children}} here',
                values: { staff: totals.staff, children: totals.children },
              })}
            </p>
            <ul className="divide-y">
              {groups.map((group) => (
                <LiveGroupRow key={group.group_id} group={group} locale={i18n.locale} />
              ))}
            </ul>
          </>
        )}
        <Link
          to={`/shifts?tab=coverage&date=${today}`}
          className="inline-block text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {i18n._({ id: 'dashboard.live.openCoverage', message: 'Open shift coverage' })}
        </Link>
      </CardContent>
    </Card>
  );
}

function LiveGroupRow({ group, locale }: { group: shifts_LiveGroupCoverageDTO; locale: string }) {
  const { i18n } = useLingui();
  return (
    <li className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 py-2">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{group.group_name}</p>
        <p className="text-xs text-muted-foreground tabular-nums">
          {i18n._({
            id: 'dashboard.live.group',
            message: '{children, plural, one {# child} other {# children}} · {staff} of {required} staff',
            values: {
              children: group.present_children,
              staff: formatNumber(group.present_staff, locale),
              required: formatNumber(group.required_staff, locale, { maximumFractionDigits: 1 }),
            },
          })}
        </p>
      </div>
      <CoverageStatusBadge status={group.status} />
    </li>
  );
}
